// Load 'Outfit Variable' (the app's primary font) ourselves instead of via
// @fontsource-variable/outfit's index.css, which ships `font-display: swap`. swap paints the system
// fallback first and then swaps to Outfit — a visible metric jump on the first (cold) open, e.g. the
// Crown Rips loader's "Opening packs…" reading bigger for a beat, then shrinking.
//
// Two changes fix that app-wide:
//  • font-display: optional → the browser gives the font a short block window and NEVER swaps it in
//    late, so text metrics can't jump. Cold first load may fall back for that session (rare — the
//    woff2 is same-origin and bundled); every cached open uses the real font immediately.
//  • preload the woff2 so it usually arrives inside the block window and the real font wins first paint.
//
// The faces are registered under BOTH 'Outfit Variable' and bare 'Outfit'. A lot of older styles
// (trail, PrizeDrop, Challenges, Dialog, sports PowerPicks) ask for bare 'Outfit', which is a
// different family name and used to resolve only via the Google Fonts stylesheet. That stylesheet is
// gone now — without the alias those rules silently fall back to system sans while everything else
// renders in Outfit, which is exactly the mismatched-font look in metagames.
import latinExt from '@fontsource-variable/outfit/files/outfit-latin-ext-wght-normal.woff2?url';
import latin from '@fontsource-variable/outfit/files/outfit-latin-wght-normal.woff2?url';

const FAMILIES = ['Outfit Variable', 'Outfit'];

const LATIN_RANGE =
  'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
const LATIN_EXT_RANGE =
  'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF';

const face = (url: string, range: string) =>
  FAMILIES.map(
    (family) =>
      `@font-face{font-family:'${family}';font-style:normal;font-weight:100 900;font-display:optional;` +
      `src:url(${url}) format('woff2-variations');unicode-range:${range};}`,
  ).join('');

if (typeof document !== 'undefined') {
  // High-priority fetch so the font is ready within optional's block window on the first paint.
  for (const href of [latin, latinExt]) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    link.href = href;
    document.head.appendChild(link);
  }
  const style = document.createElement('style');
  style.textContent = face(latin, LATIN_RANGE) + face(latinExt, LATIN_EXT_RANGE);
  document.head.appendChild(style);
}
